import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import './styles.scss';
import debounce from 'lodash/debounce';
import uuid from 'react-uuid';

/**
 * Habilita un scroll mediante flechas en los laterales
 * @param {*} props
 */
const HorizontalScroll = props => {
  const {
    children,
    className,
    arrowVisibility,
    selector,
    mobileArrows,
    mobileScroll,
    showSelector,
    id,
    buttonsSelector
  } = props;
  const [visibleArrows, setVisibleArrows] = useState(true);
  const [activeDirs, setActiveDirs] = useState({ right: true, left: false });
  const [containers, setContainers] = useState({
    itemsContainer: null,
    mainContainer: null,
    buttonsContainer: null
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [currentPos, setCurrentPos] = useState(0);
  const [horizontalActive, setHorizontalActive] = useState(false);
  const [customUuid] = useState(uuid());
  const [hasAuxCards, setHasAuxCards] = useState(false);

  const { itemsContainer, mainContainer, buttonsContainer } = containers;

  const containerWidth = itemsContainer ? itemsContainer.clientWidth : 0;
  const containerScrollWidth = itemsContainer ? itemsContainer.scrollWidth : 0;

  const getItems = useCallback(() => {
    const items = itemsContainer.children;
    return items;
  }, [itemsContainer]);

  const byPercet = d => {
    if (itemsContainer) {
      let pos = 0;
      if (id.includes('modEspecial--interesting')) {
        const items = getItems();
        const hasItems = items && items[0];
        const itemWidth = hasItems ? items[0].offsetWidth : 0;
        const exactCurrentPosition = currentStep * itemWidth;

        if (d === 'right') {
          const containerLength = hasAuxCards
            ? itemsContainer.children.length - 1
            : itemsContainer.children.length;

          if (hasItems) {
            pos =
              currentStep < containerLength - 1
                ? exactCurrentPosition + itemWidth
                : exactCurrentPosition;
          }
        } else {
          pos =
            currentStep > 0
              ? exactCurrentPosition - itemWidth
              : exactCurrentPosition;
        }
      } else {
        const container = itemsContainer;
        const position = container.scrollLeft;
        const width = container.clientWidth;
        const items = getItems();
        const end = position + width;
        if (d === 'right') {
          const lastVisibleItem = [...items].find(
            it => it.offsetLeft + it.offsetWidth > end
          );
          if (lastVisibleItem) {
            pos = lastVisibleItem.offsetLeft;
          }
        } else {
          pos = position - width;
        }
      }
      return pos;
    }
    return 0;
  };

  const checkArrowsToggle = useCallback(
    pos => {
      if (itemsContainer) {
        if (id.includes('modEspecial--interesting')) {
          const position = pos === undefined ? currentPos : pos;
          const canScrollRight = hasAuxCards
            ? currentStep !== getItems().length - 2
            : currentStep !== getItems().length - 1;

          if (position > 0 || canScrollRight) {
            setHorizontalActive(true);
          } else {
            setHorizontalActive(false);
          }

          setActiveDirs({
            left: position > 0,
            right: canScrollRight
          });
        } else {
          const container = itemsContainer;
          const position = pos === undefined ? currentPos : pos;
          const canScrollRight =
            position + container.clientWidth < container.scrollWidth;

          if (position > 0 || canScrollRight) {
            setHorizontalActive(true);
          } else {
            setHorizontalActive(false);
          }

          setActiveDirs({
            left: position > 0,
            right: canScrollRight
          });
        }
      }
    },
    [currentPos, itemsContainer, currentStep, hasAuxCards, getItems, id]
  );

  const scroll = direction => {
    const container = itemsContainer;

    if (container.clientWidth < container.scrollWidth) {
      const position = byPercet(direction);
      container.scroll({
        top: 0,
        left: position,
        behavior: 'smooth'
      });
    }
  };

  const getCurrentStep = useCallback(() => {
    const container = itemsContainer;
    if (container) {
      const childNodes = [...container.children];
      const currentEnd = container.scrollLeft + container.clientWidth;
      const currentVisibleItem = [...childNodes].find(
        e =>
          e.offsetLeft >= container.scrollLeft &&
          e.offsetLeft + e.clientWidth <= currentEnd
      );
      return childNodes.indexOf(currentVisibleItem);
    }
    return 0;
  }, [itemsContainer]);

  const renderSelector = useCallback(() => {
    if (itemsContainer) {
      const steps = hasAuxCards
        ? itemsContainer.children.length - 1
        : itemsContainer.children.length;
      if (steps) {
        return (
          <div className="selector">
            {new Array(steps).fill().map((e, i) => {
              const active = currentStep === i;
              return (
                <div
                  className={`selector-item ${
                    active ? 'selector-item--active' : ''
                  }`}
                />
              );
            })}
          </div>
        );
      }
    }
    return null;
  }, [currentStep, itemsContainer, hasAuxCards]);

  const renderArrows = () => (
    <div className="arrows-container">
      <button
        type="button"
        className={`arrow-button arrow-left ${
          !activeDirs.left ? 'removed' : ''
        }`}
        onClick={() => scroll('left')}
      />

      <button
        type="button"
        className={`arrow-button arrow-right ${
          !activeDirs.right ? 'removed' : ''
        }`}
        onClick={() => scroll('right')}
      />
    </div>
  );

  const hasWidthOverflow = useCallback(
    element =>
      element.clientWidth > 0 && element.scrollWidth > element.clientWidth,
    []
  );

  const checkArrowsVisibility = useCallback(() => {
    if (itemsContainer) {
      const hasOverflow = hasWidthOverflow(itemsContainer);
      if (hasOverflow && !visibleArrows) {
        setVisibleArrows(true);
      } else if (!hasOverflow && visibleArrows) {
        setVisibleArrows(false);
      }
    }
  }, [visibleArrows, hasWidthOverflow, itemsContainer]);

  const handleResize = useCallback(() => {
    checkArrowsVisibility();
    setCurrentStep(getCurrentStep());
  }, [checkArrowsVisibility, getCurrentStep]);

  const handleScroll = useCallback(e => {
    setCurrentPos(e.target.scrollLeft);
  }, []);

  useEffect(() => {
    checkArrowsVisibility();
    checkArrowsToggle();
  }, [
    checkArrowsVisibility,
    checkArrowsToggle,
    containerWidth,
    containerScrollWidth
  ]);

  useEffect(() => {
    const resize = debounce(handleResize, 300);
    const scrollF = debounce(handleScroll, 300);
    window.addEventListener('resize', resize);
    const container = itemsContainer;
    if (itemsContainer) {
      container.addEventListener('scroll', scrollF);
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (container) {
        container.removeEventListener('scroll', scrollF);
      }
    };
  }, [handleResize, handleScroll, itemsContainer]);

  useEffect(() => {
    setCurrentStep(getCurrentStep());
    checkArrowsToggle();
  }, [getCurrentStep, checkArrowsToggle]);

  useEffect(() => {
    if (id && selector) {
      // los HorizontalScroll del mismo tipo (p.e: modEspecial--interesting ) tienen el mismo id, se le asigna un uuid
      let main = document.getElementById(`${id}-custom-uuid-${customUuid}`);
      if (!main) {
        main = document.getElementById(id);
      }
      if (!main.getAttribute('id').includes('custom-uuid')) {
        main.setAttribute('id', `${id}-custom-uuid-${customUuid}`);
      }
      const itemsC = main.querySelector(`${selector}`);
      itemsC.classList.add('horizontalContainer');

      let buttonsS = null;
      if (buttonsSelector) {
        buttonsS = main.querySelector(buttonsSelector);
      }
      if (id.includes('modEspecial--interesting')) {
        const visibleContainerWidth =
          itemsContainer !== null ? itemsContainer.offsetWidth : 0;
        if (visibleContainerWidth > 0) {
          const auxInDom = main.querySelector('.aux-carousel-card');
          if (auxInDom) {
            const { parentNode } = auxInDom;
            parentNode.removeChild(auxInDom);
          }

          const div = main.querySelector('.horizontalContainer');
          if (div !== undefined && div !== null) {
            const items = getItems();
            if (items !== null && items !== undefined) {
              const widthCard = items[0].offsetWidth;
              const cardsPerPage = visibleContainerWidth / widthCard;
              const anchoAux = widthCard * (cardsPerPage - 1);
              const divCard = document.createElement('div');
              divCard.style.minWidth = `${anchoAux}px`;
              divCard.setAttribute('class', 'aux-carousel-card');
              div.appendChild(divCard);
              setHasAuxCards(true);
            }
          }
        }
      }

      setContainers({
        itemsContainer: itemsC,
        mainContainer: main,
        buttonsContainer: buttonsS
      });
    }
  }, [
    id,
    selector,
    buttonsSelector,
    containerWidth,
    itemsContainer,
    getItems,
    customUuid
  ]);

  // Controlamos el valor "false" porque nos puede llegar asi desde back
  const showMobileArrows = mobileArrows && mobileArrows !== 'false';
  const showMobileScroll = mobileScroll && mobileScroll !== 'false';
  const withSelector = showSelector && showSelector !== 'false';

  // Añadimos las clases necesarias al contenedor principal
  if (mainContainer) {
    const classList = ['HorizontalScroll'];
    if (className) {
      classList.push(className);
    }

    if (!showMobileArrows) {
      classList.push('HorizontalScroll__hidden-arrows');
    }

    if (!showMobileScroll) {
      classList.push('HorizontalScroll__hidden-scroll');
    }

    if (horizontalActive) {
      classList.push('activeHorizontal');
    }
    const list = mainContainer.classList;

    classList.forEach(c => {
      list.add(c);
    });
  }

  if (itemsContainer) {
    itemsContainer.classList.add('horizontalContainer');
  }

  return (
    <>
      {children}
      {/* {buttonsContainer &&
        ReactDOM.createPortal(renderArrows(), buttonsContainer)}
      {buttonsContainer &&
        withSelector &&
        ReactDOM.createPortal(renderSelector(), buttonsContainer)} */}
      {mainContainer &&
        ReactDOM.createPortal(
          <>
            {!buttonsContainer && arrowVisibility && renderArrows()}
            {!buttonsContainer && withSelector && renderSelector()}
          </>,
          mainContainer
        )}
    </>
  );
};

HorizontalScroll.propTypes = {
  children: PropTypes.node,

  /**
   * Identifica el contenedor de los items
   */
  selector: PropTypes.string.isRequired,

  /**
   * Clase que se añade al contenedor principal
   */
  className: PropTypes.string,

  /**
   * Mostrar o no las flechas
   */
  arrowVisibility: PropTypes.bool,

  /**
   * Mostrar o no las flechas en mobile
   */
  mobileArrows: PropTypes.bool,

  /**
   *
   */
  mobileScroll: PropTypes.bool,

  /**
   * Mostrar o no los puntos que indican los items que hay y el activo
   */
  showSelector: PropTypes.bool,

  /**
   * Lo usamos para identificar el contenedor principal
   */
  id: PropTypes.string,

  /**
   * Selector en el que agrupar los controles del carrusel
   */
  buttonsSelector: PropTypes.string
};

HorizontalScroll.defaultProps = {
  className: '',
  arrowVisibility: true,
  children: undefined,
  mobileArrows: false,
  mobileScroll: true,
  showSelector: false,
  id: null,
  buttonsSelector: null
};

export default HorizontalScroll;
